(function () {
  'use strict';

  /**
   * Convert a price in cents to a fixed-decimal string.
   * @param {number} number - Price in cents (e.g., 74591)
   * @param {number} [precision=2] - Number of decimal places
   * @returns {string|number} Formatted price string, or 0 for falsy/NaN input
   */
  function formatPriceWithCents(number, precision) {
    if (precision === undefined) precision = 2;
    if (Number.isNaN(number) || !number) return 0;
    return (number / 100.0).toFixed(precision);
  }

  /**
   * Derive the shop region from the hostname.
   * @returns {string} e.g., 'us' from 'us-stage.zwift.com'
   */
  function getShopRegion() {
    return window.location.hostname.split('-')[0].split('.')[0];
  }

  /**
   * Pick a product category from collections, preferring the 2nd collection.
   * @param {Array<{title: string}>} collections
   * @returns {string|undefined}
   */
  function getProductCategoryFromCollections(collections) {
    if (!collections) return undefined;
    return (collections[1] && collections[1].title) || (collections[0] && collections[0].title);
  }

  /**
   * Read the currently viewed product from the Zwift global.
   * @returns {Object|undefined}
   */
  function getProductViewed() {
    return (window.Zwift && window.Zwift.product) || undefined;
  }

  /**
   * Read the Shopify cart id from cookies.
   * @returns {string|undefined}
   */
  function getCartId() {
    var match = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
    return match ? match[1] : undefined;
  }

  /**
   * Track an event via the Segment analytics global, if available.
   * @param {string} event
   * @param {Object} payload
   */
  function track(event, payload) {
    if (!window.analytics || typeof window.analytics.track !== 'function') return;
    window.analytics.track(event, payload);
  }

  /**
   * Build the "Product Viewed" payload from the currently viewed product.
   * @returns {Object|null} payload, or null when no product is present
   */
  function buildProductViewedPayload() {
    var product = getProductViewed();
    if (!product) return null;
    return {
      brand: product.vendor,
      category: getProductCategoryFromCollections(product.collections),
      name: product.title,
      price: formatPriceWithCents(product.price),
      product_id: product.id,
      sku: product.variants && product.variants[0] && product.variants[0].sku,
      store: getShopRegion(),
      variant: product.variants && product.variants[0] && product.variants[0].id,
    };
  }

  /**
   * Fire the "Product Viewed" event when a product is present.
   */
  function fireProductViewed() {
    var payload = buildProductViewedPayload();
    if (payload) track('Product Viewed', payload);
  }

  /**
   * Build an analytics payload from a cart line item.
   * @param {Object} item - A /cart.js line item
   * @param {number} quantity - The quantity delta to report
   * @returns {Object} payload
   */
  function lineToPayload(item, quantity) {
    var viewed = getProductViewed();
    return {
      brand: item.vendor,
      cart_id: getCartId(),
      category: viewed && getProductCategoryFromCollections(viewed.collections),
      name: item.product_title || item.title,
      price: formatPriceWithCents(item.price),
      product_id: item.product_id,
      quantity: quantity,
      sku: item.sku,
      store: getShopRegion(),
      variant: item.variant_id,
    };
  }
  function buildProductAddedPayload(item, quantity) { return lineToPayload(item, quantity || 1); }

  var cartSnapshot = {}; // key -> { item, quantity }

  function indexCart(items) {
    var map = {};
    (items || []).forEach(function (it) { map[it.key] = { item: it, quantity: it.quantity }; });
    return map;
  }

  function diffAndTrack(newItems) {
    var next = indexCart(newItems);
    Object.keys(next).forEach(function (key) {
      var before = cartSnapshot[key] ? cartSnapshot[key].quantity : 0;
      var delta = next[key].quantity - before;
      if (delta > 0) track('Product Added', lineToPayload(next[key].item, delta));
    });
    Object.keys(cartSnapshot).forEach(function (key) {
      var after = next[key] ? next[key].quantity : 0;
      var delta = cartSnapshot[key].quantity - after;
      if (delta > 0) track('Product Removed', lineToPayload(cartSnapshot[key].item, delta));
    });
    cartSnapshot = next;
  }

  function cartUrl() {
    return ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js';
  }

  function refreshCart(thenTrack) {
    if (typeof fetch !== 'function') return Promise.resolve();
    return fetch(cartUrl())
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (thenTrack) diffAndTrack(cart.items);
        else cartSnapshot = indexCart(cart.items);
      })
      .catch(function (e) { console.error('analytics cart fetch failed', e); });
  }

  // Serialize cart refreshes. Overlapping cart:update events (rapid qty changes,
  // multi-line quick-add) must diff against the updated snapshot, not the same
  // stale one — otherwise the same delta is counted twice (duplicate events).
  var cartRefreshQueue = Promise.resolve();
  function enqueueRefresh(thenTrack) {
    cartRefreshQueue = cartRefreshQueue.then(function () { return refreshCart(thenTrack); });
    return cartRefreshQueue;
  }

  /**
   * Seed the cart snapshot, then derive Product Added / Product Removed by
   * diffing the cart from /cart.js on each cart:update (refreshes serialized).
   */
  function initCartTracking() {
    enqueueRefresh(false);
    document.addEventListener('cart:update', function (event) {
      var data = (event.detail && event.detail.data) || {};
      if (data.didError) return;
      enqueueRefresh(true);
    });
  }

  /**
   * Wire the "Checkout Step" event to the cart checkout button click.
   * Reads the cart via /cart.js since Horizon has no global line-item map.
   */
  function initCheckoutTracking() {
    document.addEventListener('click', function (event) {
      // Only the real checkout submit button (logged-in). The guest "Create Account" /
      // "Sign In" links in zwift-checkout-guard also use id="checkout"/"sign-in" but are
      // <a> elements without name="checkout" — they must NOT fire a checkout event.
      var target = event.target && event.target.closest ? event.target.closest('button#checkout') : null;
      if (!target) return;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart.js')
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var items = (cart.items || []).map(function (item) {
            return {
              brand: item.vendor,
              name: item.product_title || item.title,
              price: formatPriceWithCents(item.price),
              product_id: item.product_id,
              quantity: item.quantity,
              sku: item.sku,
              variant: item.variant_id,
            };
          });
          if (!items.length) return;
          track('Checkout Step', {
            currency: window.Shopify && window.Shopify.currency && window.Shopify.currency.active,
            cart_id: getCartId(),
            checkout_step: 1,
            products: items,
            stepDescription: 'cart',
            store: getShopRegion(),
          });
        })
        .catch(function (e) {
          console.error('initCheckoutTracking(): failed to send Checkout Step', e);
        });
    });
  }

  /**
   * Normalize a locale to lang-REGION form (e.g., 'en-us' -> 'en-US').
   * @param {string} locale
   * @returns {string|undefined}
   */
  function getAnalyticsLocale(locale) {
    if (!locale) return undefined;
    var parts = locale.split('-');
    if (parts.length < 2) return locale; // no region → leave as-is
    return parts[0] + '-' + parts[1].toUpperCase();
  }

  /**
   * Read the logged-in user from the Zwift global.
   * @returns {Object}
   */
  function getUser() {
    return (window.Zwift && window.Zwift.user) || {};
  }

  /**
   * Identify the logged-in user (if any) and send a Segment page view so
   * subsequent events are attributed to the user instead of anonymous.
   */
  function fireAuthStatus() {
    var user = getUser();
    var rawLocale = (window.Zwift && window.Zwift.publicEnv && window.Zwift.publicEnv.LOCALE) || undefined;
    var locale = getAnalyticsLocale(rawLocale);
    var data = {
      name: document.location.pathname,
      loggedInStatus: user.isLoggedIn ? 'Logged In' : 'Logged Out',
      isAuthenticated: !!user.isLoggedIn,
      language: locale,
      locale: locale,
      referral_page_id: document.referrer,
      userId: user.isLoggedIn ? user.zwiftId : undefined,
      'Auth State': user.isLoggedIn ? 'Auth' : 'Unauth',
    };
    if (user.isLoggedIn && window.analytics && typeof window.analytics.identify === 'function') {
      var traits = { locale: locale };
      if (user.email) traits.email = user.email;
      window.analytics.identify(user.zwiftId, traits);
    }
    if (window.analytics && typeof window.analytics.page === 'function') {
      window.analytics.page(data);
    }
  }

  /**
   * Fire the "Account Creation Flow" event when a guest clicks the cart
   * "Create Account" CTA (rendered by zwift-checkout-guard with
   * data-analytics="create-account").
   */
  function initAccountCreationTracking() {
    document.addEventListener('click', function (event) {
      var target = event.target && event.target.closest ? event.target.closest('[data-analytics="create-account"]') : null;
      if (!target) return;
      track('Account Creation Flow', { accountCreationStep: 1 });
    });
  }

  // Exposed for testing and for the event-wiring added in later tasks
  window.ZwiftAnalytics = {
    formatPriceWithCents: formatPriceWithCents,
    getShopRegion: getShopRegion,
    getProductCategoryFromCollections: getProductCategoryFromCollections,
    getProductViewed: getProductViewed,
    getCartId: getCartId,
    track: track,
    buildProductViewedPayload: buildProductViewedPayload,
    fireProductViewed: fireProductViewed,
    buildProductAddedPayload: buildProductAddedPayload,
    lineToPayload: lineToPayload,
    initCartTracking: initCartTracking,
    initCheckoutTracking: initCheckoutTracking,
    initAccountCreationTracking: initAccountCreationTracking,
    getAnalyticsLocale: getAnalyticsLocale,
    getUser: getUser,
    fireAuthStatus: fireAuthStatus,
  };

  // --- Auto-initialization ---
  if (typeof document !== 'undefined') {
    initCartTracking();
    initCheckoutTracking();
    initAccountCreationTracking();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fireAuthStatus);
    } else {
      fireAuthStatus();
    }
    if (window.Zwift && window.Zwift.product) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', fireProductViewed);
      } else {
        fireProductViewed();
      }
    }
  }
})();
